import { api } from './api';

export type FeedbackCategory = 'bug' | 'idea' | 'process' | 'question';
export type FeedbackStatus =
  | 'new'
  | 'responded'
  | 'reviewing'
  | 'planned'
  | 'done'
  | 'declined';

/** One turn in a ticket's thread (Feedback V2, Jim 2026-09-04). */
export type FeedbackMessage = {
  id: string;
  ticket_id: string;
  author_id: string | null;
  author_name: string | null;
  author_role: string | null;
  is_admin: boolean;
  body: string;
  attachment_s3_keys: string[];
  attachment_urls: string[];
  created_at: string;
};

export type FeedbackTicket = {
  id: string;
  author_id: string | null;
  author_name: string | null;
  author_role: string | null;
  source: 'hub' | 'lite';
  category: FeedbackCategory;
  title: string | null;
  body: string;
  route: string | null;
  site_id: string | null;
  site_name: string | null;
  inspection_id: string | null;
  photo_s3_keys: string[];
  photo_urls: string[];
  status: FeedbackStatus;
  admin_note: string | null;
  created_at: string;
  updated_at: string;
  messages: FeedbackMessage[];
  unread_replies: number;
  last_message_at: string | null;
};

export type FeedbackCreate = {
  category: FeedbackCategory;
  body: string;
  title?: string;
  source: 'hub' | 'lite';
  route?: string;
  site_id?: string | null;
  inspection_id?: string | null;
  photo_s3_keys?: string[];
};

export const feedbackService = {
  async create(data: FeedbackCreate): Promise<FeedbackTicket> {
    const r = await api.post<FeedbackTicket>('/feedback', data);
    return r.data;
  },
  /** Photo, snip or short video clip — one file per call. */
  async uploadPhoto(file: File): Promise<{ s3_key: string }> {
    const form = new FormData();
    form.append('file', file);
    const r = await api.post<{ s3_key: string }>('/feedback/uploads', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
  async list(params: {
    status?: string;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    items: FeedbackTicket[];
    total: number;
    counts: Record<string, number>;
  }> {
    const r = await api.get<{
      items: FeedbackTicket[];
      total: number;
      counts: Record<string, number>;
    }>('/feedback', { params });
    return r.data;
  },
  async update(
    id: string,
    data: { status?: FeedbackStatus; admin_note?: string }
  ): Promise<FeedbackTicket> {
    const r = await api.patch<FeedbackTicket>(`/feedback/${id}`, data);
    return r.data;
  },
  /** The signed-in user's own tickets with their threads, newest activity first. */
  async mine(): Promise<FeedbackTicket[]> {
    const r = await api.get<FeedbackTicket[]>('/feedback/mine');
    return r.data;
  },
  /** Admin replies the user hasn't opened — the light-bulb badge. */
  async unreadCount(): Promise<{ unread: number; tickets: number }> {
    const r = await api.get<{ unread: number; tickets: number }>(
      '/feedback/unread-count'
    );
    return r.data;
  },
  async get(id: string): Promise<FeedbackTicket> {
    const r = await api.get<FeedbackTicket>(`/feedback/${id}`);
    return r.data;
  },
  async reply(
    id: string,
    data: { body: string; attachment_s3_keys?: string[] }
  ): Promise<FeedbackTicket> {
    const r = await api.post<FeedbackTicket>(`/feedback/${id}/messages`, data);
    return r.data;
  },
  async markRead(id: string): Promise<FeedbackTicket> {
    const r = await api.post<FeedbackTicket>(`/feedback/${id}/read`, {});
    return r.data;
  },
};
